import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { BarChart3, Plus, Repeat, ArrowDownRight, ArrowUpRight } from 'lucide-react';

const ForecastTab = ({
  cashflowForecast,
  recurringItems,
  formatCurrency,
  onAddExpense,
  onAddIncome,
}) => (
  <div className="space-y-6">
    {/* Summary Card */}
    <Card className="border-[#B4C9BE] bg-gradient-to-r from-indigo-50 to-purple-50">
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-[#E2ECE7] rounded-lg">
            <BarChart3 className="w-5 h-5 text-[#0A2B1D]" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">6-Month Cash Flow Forecast</h3>
            <p className="text-sm text-gray-600">
              Based on {cashflowForecast?.summary?.recurring_expense_count || 0} recurring expenses and {cashflowForecast?.summary?.recurring_income_count || 0} recurring income items
            </p>
          </div>
        </div>
        
        {cashflowForecast && (
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="text-center p-3 bg-white rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Projected Income</p>
              <p className="text-xl font-bold text-green-600">{formatCurrency(cashflowForecast.summary?.total_projected_income)}</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Projected Expenses</p>
              <p className="text-xl font-bold text-red-600">{formatCurrency(cashflowForecast.summary?.total_projected_expenses)}</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Projected Net</p>
              <p className={`text-xl font-bold ${cashflowForecast.summary?.total_projected_net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(cashflowForecast.summary?.total_projected_net)}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Monthly Forecast Chart */}
    {cashflowForecast?.forecast?.length > 0 ? (
      <Card>
        <CardHeader>
          <CardTitle>Monthly Projection</CardTitle>
          <CardDescription>Expected cash flow based on recurring entries</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {cashflowForecast.forecast.map((month) => (
              <div key={month.month} className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-gray-900">{month.month}</h4>
                  <span className={`text-sm font-medium px-2 py-1 rounded ${month.projected_net >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    Net: {formatCurrency(month.projected_net)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Income</p>
                    <p className="text-lg font-medium text-green-600">{formatCurrency(month.projected_income)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Expenses</p>
                    <p className="text-lg font-medium text-red-600">{formatCurrency(month.projected_expenses)}</p>
                  </div>
                </div>
                {/* Progress bar visualization */}
                <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden flex">
                  <div 
                    className="bg-green-500 h-full" 
                    style={{ width: `${(month.projected_income / Math.max(month.projected_income, month.projected_expenses, 1)) * 50}%` }}
                  />
                  <div 
                    className="bg-red-500 h-full" 
                    style={{ width: `${(month.projected_expenses / Math.max(month.projected_income, month.projected_expenses, 1)) * 50}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    ) : (
      <Card className="text-center py-12">
        <CardContent>
          <div className="w-16 h-16 bg-[#E2ECE7] rounded-full flex items-center justify-center mx-auto mb-4">
            <Repeat className="w-8 h-8 text-[#0A2B1D]" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Recurring Items</h3>
          <p className="text-gray-500 mb-4">
            Add recurring expenses or income to see your cash flow forecast.
            <br />
            Mark items as "Recurring" when adding them.
          </p>
          <div className="flex gap-2 justify-center">
            <Button onClick={onAddExpense} variant="outline">
              <Plus className="w-4 h-4 mr-1" /> Add Recurring Expense
            </Button>
            <Button onClick={onAddIncome} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm">
              <Plus className="w-4 h-4 mr-1" /> Add Recurring Income
            </Button>
          </div>
        </CardContent>
      </Card>
    )}

    {/* Recurring Items Summary */}
    {(recurringItems.recurring_expenses.length > 0 || recurringItems.recurring_income.length > 0) && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Repeat className="w-5 h-5 text-[#0A2B1D]" />
            Recurring Items
          </CardTitle>
          <CardDescription>Your recurring expenses and income sources</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Recurring Expenses */}
            <div>
              <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                <ArrowDownRight className="w-4 h-4 text-red-500" />
                Recurring Expenses ({recurringItems.recurring_expenses.length})
              </h4>
              <div className="space-y-2">
                {recurringItems.recurring_expenses.map((exp) => (
                  <div key={exp.id || `${exp.description}-${exp.amount}`} className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{exp.description}</p>
                      <p className="text-sm text-gray-500">{exp.category_name} • {exp.recurring_frequency || 'monthly'}</p>
                    </div>
                    <p className="font-semibold text-red-600">{formatCurrency(exp.amount)}</p>
                  </div>
                ))}
                {recurringItems.recurring_expenses.length === 0 && (
                  <p className="text-gray-400 text-sm">No recurring expenses</p>
                )}
              </div>
            </div>
            
            {/* Recurring Income */}
            <div>
              <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                <ArrowUpRight className="w-4 h-4 text-green-500" />
                Recurring Income ({recurringItems.recurring_income.length})
              </h4>
              <div className="space-y-2">
                {recurringItems.recurring_income.map((inc) => (
                  <div key={inc.id || `${inc.description}-${inc.amount}`} className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{inc.description}</p>
                      <p className="text-sm text-gray-500">{inc.category_name} • {inc.recurring_frequency || 'monthly'}</p>
                    </div>
                    <p className="font-semibold text-green-600">{formatCurrency(inc.amount)}</p>
                  </div>
                ))}
                {recurringItems.recurring_income.length === 0 && (
                  <p className="text-gray-400 text-sm">No recurring income</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    )}
  </div>
);

export default ForecastTab;
